import React, { useState, useEffect } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from 'recharts';
import { TrendingUp, Target, Users, Building2, Calendar, CheckCircle, Download } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import api from '../services/api';
import { exportToCSV } from '../utils/export';
import { formatCurrency } from '../utils/format';

const STAGE_LABELS = { lead: 'Lead', qualified: 'Qualifié', proposal: 'Proposition', negotiation: 'Négociation', won: 'Gagné', lost: 'Perdu' };
const STAGE_COLORS = { lead: '#6366F1', qualified: '#3B82F6', proposal: '#FFAB00', negotiation: '#F97316', won: '#1B99E5', lost: '#EF4444' };
const ACTIVITY_COLORS = { call: '#1B99E5', email: '#3B82F6', meeting: '#6366F1', task: '#F97316', note: '#94a3b8' };

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white dark:bg-[#1A1D23] border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg p-3 text-xs">
      <p className="font-semibold text-gray-900 dark:text-white mb-1">{label}</p>
      {payload.map((p, i) => (
        <p key={i} style={{ color: p.color || p.fill }}>{p.name}: <strong>{p.value?.toLocaleString('fr-FR')}</strong></p>
      ))}
    </div>
  );
};

const StatCard = ({ icon: Icon, title, value, subtitle, color }) => (
  <div className="bg-white dark:bg-[#1A1D23] rounded-xl border border-gray-200 dark:border-gray-800 p-5">
    <div className="flex items-center justify-between mb-3">
      <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${color}20` }}>
        <Icon size={16} style={{ color }} />
      </div>
    </div>
    <p className="text-xl font-bold text-gray-900 dark:text-white" style={{ fontFamily: 'Manrope, sans-serif' }}>{value}</p>
    <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mt-0.5">{title}</p>
    {subtitle && <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">{subtitle}</p>}
  </div>
);

export default function Reports() {
  const [stats, setStats] = useState(null);
  const [monthly, setMonthly] = useState([]);
  const [actsByType, setActsByType] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get('/reports/summary'),
      api.get('/reports/monthly'),
      api.get('/reports/activities-by-type')
    ]).then(([s, m, a]) => {
      setStats(s.data);
      setMonthly(m.data.map(d => ({
        month: d._id,
        Devis: d.count,
        'Valeur (k€)': Math.round(d.value / 1000),
        'Gagné (k€)': Math.round(d.won / 1000)
      })));
      setActsByType(a.data.map(d => ({
        name: { call: 'Appel', email: 'Email', task: 'Tâche', note: 'Note' }[d._id] || d._id,
        value: d.count,
        color: ACTIVITY_COLORS[d._id] || '#94a3b8'
      })));
    }).catch(console.error).finally(() => setLoading(false));
  }, []);

  const handleExportReport = () => {
    if (!stats) { toast.error('Aucune donnée à exporter'); return; }
    const summary = [
      { Indicateur: "Chiffre d'affaires", Valeur: stats.total_revenue || 0, Unite: '€' },
      { Indicateur: 'Valeur pipeline', Valeur: stats.pipeline_value || 0, Unite: '€' },
      { Indicateur: 'Taux de conversion', Valeur: stats.conversion_rate || 0, Unite: '%' },
      { Indicateur: 'Total devis', Valeur: stats.total_deals || 0, Unite: '' },
      { Indicateur: 'Devis gagnés', Valeur: stats.won_deals || 0, Unite: '' },
      { Indicateur: 'Devis perdus', Valeur: stats.lost_deals || 0, Unite: '' },
      { Indicateur: 'Contacts', Valeur: stats.total_contacts || 0, Unite: '' },
      { Indicateur: 'Entreprises', Valeur: stats.total_companies || 0, Unite: '' },
      { Indicateur: 'Activités en attente', Valeur: stats.pending_activities || 0, Unite: '' },
    ];
    exportToCSV(summary, `rapport_crm_${new Date().toISOString().substring(0,10)}.csv`);
    toast.success('Rapport exporté');
  };

  const pieData = stats?.deals_by_stage?.filter(d => d.count > 0).map(d => ({
    name: STAGE_LABELS[d._id] || d._id,
    value: d.count,
    color: STAGE_COLORS[d._id] || '#94a3b8'
  })) || [];

  if (loading) {
    return (
      <div className="p-6">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[1,2,3,4].map(i => <div key={i} className="bg-white dark:bg-[#1A1D23] rounded-xl border border-gray-200 dark:border-gray-800 h-28 animate-pulse" />)}
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white" style={{ fontFamily: 'Manrope, sans-serif' }}>Rapports & Analyses</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">Vue d'ensemble de vos performances</p>
        </div>
        <Button onClick={handleExportReport} variant="outline" className="h-9 px-4 text-sm border-gray-200 text-gray-600 hover:bg-gray-50" data-testid="export-report-btn">
          <Download size={14} className="mr-1.5" /> Exporter le rapport
        </Button>
      </div>

      {/* KPI Row */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={TrendingUp} title="Chiffre d'affaires" value={formatCurrency(stats?.total_revenue)} color="#1B99E5"
          subtitle={`${stats?.won_deals || 0} devis gagnés`} />
        <StatCard icon={Target} title="Valeur pipeline" value={formatCurrency(stats?.pipeline_value)} color="#3B82F6"
          subtitle="Devis actifs" />
        <StatCard icon={CheckCircle} title="Taux de conversion" value={`${stats?.conversion_rate || 0}%`} color="#FFAB00"
          subtitle={`${stats?.total_deals || 0} devis totaux`} />
        <StatCard icon={Calendar} title="Activités en attente" value={stats?.pending_activities || 0} color="#6366F1"
          subtitle="À traiter" />
      </div>

      {/* Charts row 1 */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Monthly */}
        <div className="lg:col-span-2 bg-white dark:bg-[#1A1D23] rounded-xl border border-gray-200 dark:border-gray-800 p-5">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Évolution mensuelle des devis
          </h3>
          {monthly.length > 0 ? (
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={monthly} margin={{ top: 0, right: 0, left: -25, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
                <XAxis dataKey="month" tick={{ fontSize: 10, fill: '#9CA3AF' }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 10, fill: '#9CA3AF' }} axisLine={false} tickLine={false} />
                <Tooltip content={<CustomTooltip />} />
                <Legend iconType="square" iconSize={8} wrapperStyle={{ fontSize: '11px', paddingTop: '8px' }} />
                <Bar dataKey="Valeur (k€)" fill="#1B99E5" radius={[3,3,0,0]} />
                <Bar dataKey="Gagné (k€)" fill="#3B82F6" radius={[3,3,0,0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-48 flex items-center justify-center text-gray-400">
              <p className="text-sm">Aucune donnée mensuelle disponible</p>
            </div>
          )}
        </div>

        {/* Devis by stage */}
        <div className="bg-white dark:bg-[#1A1D23] rounded-xl border border-gray-200 dark:border-gray-800 p-5">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Devis par étape
          </h3>
          {pieData.length > 0 ? (
            <ResponsiveContainer width="100%" height={240}>
              <PieChart>
                <Pie data={pieData} cx="50%" cy="40%" innerRadius={55} outerRadius={85} paddingAngle={2} dataKey="value">
                  {pieData.map((entry, index) => <Cell key={index} fill={entry.color} />)}
                </Pie>
                <Tooltip formatter={(v, n) => [v, n]} />
                <Legend iconType="circle" iconSize={7} wrapperStyle={{ fontSize: '10px' }} />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-48 flex items-center justify-center text-gray-400">
              <p className="text-sm">Aucun devis</p>
            </div>
          )}
        </div>
      </div>

      {/* Charts row 2 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Activities by type */}
        <div className="bg-white dark:bg-[#1A1D23] rounded-xl border border-gray-200 dark:border-gray-800 p-5">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Activités par type
          </h3>
          {actsByType.length > 0 ? (
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={actsByType} layout="vertical" margin={{ left: 10, right: 20, top: 0, bottom: 0 }}>
                <XAxis type="number" tick={{ fontSize: 10, fill: '#9CA3AF' }} axisLine={false} tickLine={false} />
                <YAxis type="category" dataKey="name" tick={{ fontSize: 11, fill: '#9CA3AF' }} axisLine={false} tickLine={false} width={60} />
                <Tooltip content={<CustomTooltip />} />
                <Bar dataKey="value" radius={[0,4,4,0]} name="Nb">
                  {actsByType.map((entry, index) => <Cell key={index} fill={entry.color} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-40 flex items-center justify-center text-gray-400">
              <p className="text-sm">Aucune activité</p>
            </div>
          )}
        </div>

        {/* Summary stats table */}
        <div className="bg-white dark:bg-[#1A1D23] rounded-xl border border-gray-200 dark:border-gray-800 p-5">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Résumé global
          </h3>
          <div className="space-y-3">
            {[
              { icon: Target, label: 'Total devis', value: stats?.total_deals || 0, color: '#3B82F6' },
              { icon: TrendingUp, label: 'Devis gagnés', value: stats?.won_deals || 0, color: '#1B99E5' },
              { icon: Target, label: 'Devis perdus', value: stats?.lost_deals || 0, color: '#EF4444' },
              { icon: Users, label: 'Contacts', value: stats?.total_contacts || 0, color: '#6366F1' },
              { icon: Building2, label: 'Entreprises', value: stats?.total_companies || 0, color: '#F97316' },
            ].map(({ icon: Icon, label, value, color }) => (
              <div key={label} className="flex items-center justify-between py-1.5 border-b border-gray-100 dark:border-gray-800 last:border-0">
                <div className="flex items-center gap-2.5">
                  <div className="w-6 h-6 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${color}15` }}>
                    <Icon size={12} style={{ color }} />
                  </div>
                  <span className="text-sm text-gray-600 dark:text-gray-400">{label}</span>
                </div>
                <span className="text-sm font-bold text-gray-900 dark:text-white">{value}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

